import { z } from 'zod'

import type { Horizon } from '@/hooks/queries/bidSimulator'
import { pruned } from '@/lib/filters'

/**
 * The URL state of the bid simulator and its accuracy page.
 *
 * The store is `store`, as on every other marketing page (AMS View, Change
 * History, Dayparting). VentaHub's `store_id` (`BidSimulatorPage.tsx:117-121`)
 * is still read, so an older link opens the same store, but never written:
 * {@link searchStore} reads both and {@link storeChange} writes `store` only.
 * Every key is optional with `.catch`,
 * defaults are applied at read time and pruned on the way out (rules E9).
 *
 * The router JSON-parses values: `?store_id=164` arrives as a number and an
 * Amazon ad-group id as a number too, so text keys accept one.
 */

/** A number the router left as text, from a hand-written link. */
function toNumber(value: unknown): unknown {
  return typeof value === 'string' && value.trim() !== '' ? Number(value) : value
}

/** An Amazon id or a search the router parsed as a number. */
function toText(value: unknown): unknown {
  return typeof value === 'number' ? String(value) : value
}

export const HORIZONS = [7, 14] as const satisfies readonly Horizon[]
export const DEFAULT_HORIZON: Horizon = 7

/** Windows for *finding* keywords (`BidSimulatorPage.tsx:73-78`). None of them moves the forecast. */
export const WINDOWS = [30, 60, 90] as const
export type ListWindow = (typeof WINDOWS)[number]
export const DEFAULT_WINDOW: ListWindow = 30

/** How a keyword is found: browse first (`BidSimulatorPage.tsx:126-131`). */
export type FindWay = 'browse' | 'search'

const storeId = z.preprocess(toNumber, z.number().int().positive()).optional().catch(undefined)
const horizon = z
  .preprocess(toNumber, z.union([z.literal(7), z.literal(14)]))
  .optional()
  .catch(undefined)
const text = z.preprocess(toText, z.string().max(200)).optional().catch(undefined)

export const bidSimulatorSearchSchema = z.object({
  store: storeId.describe(
    'The store simulated, by id; default the first store in the sorted list.',
  ),
  /** VentaHub's key, read when `store` is absent; never written. */
  store_id: storeId.describe(
    'Legacy VentaHub store key, read when store is absent; never written.',
  ),
  horizon: horizon.describe('Days the forecast covers, 7 or 14; default 7.'),
  /** The keyword list's window, in days. */
  window: z
    .preprocess(toNumber, z.union([z.literal(30), z.literal(60), z.literal(90)]))
    .optional()
    .catch(undefined)
    .describe('Days of history the keyword lists cover, 30, 60 or 90; default 30.'),
  way: z
    .enum(['browse', 'search'])
    .optional()
    .catch(undefined)
    .describe('How a keyword is found: browse ad groups or search keyword text; default browse.'),
  /** The ad group opened in the browser, by its Amazon id. */
  ad_group: text.describe(
    'Amazon id of the ad group open in the browser, whose targets are listed.',
  ),
  /** The browser's ad-group filter. */
  filter: text.describe("Text filtering the browser's ad groups by ad group or campaign name."),
  /** The keyword search, as submitted. */
  q: text.describe('The submitted keyword search.'),
  /** The search row whose ad groups are listed: its text and match type. */
  open: text.describe(
    'The search row whose ad groups are listed: its keyword text and match type.',
  ),
})
export type BidSimulatorSearch = z.infer<typeof bidSimulatorSearchSchema>

/** The clean URL is the default view: defaults and empty values are dropped. */
export function pruneBidSimulatorSearch(search: BidSimulatorSearch): BidSimulatorSearch {
  return pruned({
    ...search,
    horizon: search.horizon === DEFAULT_HORIZON ? undefined : search.horizon,
    window: search.window === DEFAULT_WINDOW ? undefined : search.window,
    way: search.way === 'browse' ? undefined : search.way,
  })
}

export const bidAccuracySearchSchema = z.object({
  store: storeId.describe('The store scored, by id; default the first store in the sorted list.'),
  store_id: storeId.describe(
    'Legacy VentaHub store key, read when store is absent; never written.',
  ),
  horizon: horizon.describe('Forecast horizon scored, 7 or 14 days; default 7.'),
})
export type BidAccuracySearch = z.infer<typeof bidAccuracySearchSchema>

export function pruneBidAccuracySearch(search: BidAccuracySearch): BidAccuracySearch {
  return pruned({
    ...search,
    horizon: search.horizon === DEFAULT_HORIZON ? undefined : search.horizon,
  })
}

/** The store a page's URL names: `store`, else the older `store_id`. */
export function searchStore(search: { store?: number; store_id?: number }): number | undefined {
  return search.store ?? search.store_id
}

/** A store pick, written as `store`, the older key dropped from the URL. */
export function storeChange(store: number | undefined): { store?: number; store_id?: undefined } {
  return { store, store_id: undefined }
}
